import { useMemo } from "react";
import { getFirestore } from "store/getFirebase";
import { useCollectionData } from "hooks";
import {
  collection,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
  type SnapshotOptions,
  type DocumentData,
} from "firebase/firestore";
import { sortByPriority } from "utilities/sortMarkets";

export type MarketCategory = {
  priority: number;
  name: string;
  entries: MarketCategoryEntry[];
};
export type MarketCategoryEntry = {
  priority: number;
  marketType?: string;
  attributes?: Record<string, string>;
};

const categoryConverter: FirestoreDataConverter<MarketCategory> = {
  // we are not saving in firestore no need to transform
  toFirestore: (data: any): DocumentData => data,
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): MarketCategory => {
    const data = snapshot.data(options);

    return {
      name: data.name,
      priority: Number(data.priority),
      entries: Object.values(data.entries || {})
        .map((it: any) => {
          return {
            ...it,
            priority: Number(it.priority),
          } as MarketCategoryEntry;
        })
        .sort(sortByPriority),
    };
  },
};

export const useBettingMarketCategories = (
  sport?: string,
): [MarketCategory[], boolean] => {
  const categoriesCol = sport
    ? collection(
        getFirestore(),
        "bettingMarketCategories",
        sport,
        "categories",
      ).withConverter(categoryConverter)
    : undefined;

  const [categories, categoriesLoading, categoriesError] = useCollectionData(
    categoriesCol,
    categoriesCol?.path,
  );

  const sorted = useMemo(
    () => (categories || []).sort(sortByPriority),
    [categories],
  );

  /*
  there's an issue with useCollectionData which returns [undefined, false] on a re-render it then returns [undefined, true] on the next render.
  It is safe to force the loading state here as the useCollectionData() hook above will return an empty array when it resolves and there is no data.
  See ticket for more info - https://puntaa.atlassian.net/browse/PKB-3670
  */
  if (
    (typeof categoriesCol === "undefined" ||
      typeof categories === "undefined") &&
    typeof categoriesError === "undefined"
  ) {
    return [[], true];
  }

  return [sorted, categoriesLoading];
};
